import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import AsyncSelect from "react-select/async";
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from "axios";

// My components
import SecCard from "../../components/common/SecCard";
import Loader from "../../components/common/Loader";
import BackButton from "../../components/common/BackButton";

// My css
import css from "../../css/institute/add-insti-page.module.css";
import { SERVER_ORIGIN } from "../../utilities/constants";

const AddTermsAndConditions = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedEM, setSelectedEM] = useState(null);
    const [defaultOptions, setDefaultOptions] = useState([]);

    const [termsForm, setTermsForm] = useState({
        title: "",
        content: "",
        version: "1.0",
        financialYear: "",
        termsfor:"",
        createdBy: "",
        // emData: null,
        appliesTo: ['all'],
        tags: []
    });

    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    // ReactQuill modules configuration for rich text editing
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['blockquote', 'code-block'],
            ['clean']
        ]
    };

    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'list', 'bullet',
        'indent',
        'direction', 'align',
        'link', 'image', 'video',
        'blockquote', 'code-block'
    ];

    // Financial year options (current year and next few years)
    const generateFinancialYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -2; i <= 3; i++) {
            const startYear = currentYear + i;
            const endYear = startYear + 1;
            years.push({
                value: `${startYear}-${endYear.toString().slice(-2)}`,
                label: `${startYear}-${endYear.toString().slice(-2)}`
            });
        }
        return years;
    };

    const financialYearOptions = generateFinancialYears();

  const onChange = (e) => {
        const { name, value } = e.target;
        setTermsForm({ ...termsForm, [name]: value });

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const loadOptions = async (inputValue, callback) => {
        try {
            const params = {
                search: inputValue,
                page: 1,
                limit: 10,
                sortBy: "name",
                sortType: "asc"
            };

            const response = await axios.get(`${SERVER_ORIGIN}/api/em/auth/em/all`, {
                params,
                headers: {
                    'auth-token': sessionStorage.getItem('token'),
                }
            });

            const options = response.data.filteredInstitutes.map(em => ({
                label: em.name,
                value: em._id,
                emData: {
                    chapterName: em.chapterName,
                    regionName: em.region,
                    emName: em.name,
                    emId: em._id
                }
            }));
            callback(options);
        } catch (error) {
            console.error("Error fetching EMs", error);
            callback([]);
        }
    };

    useEffect(() => {
        const fetchDefaultOptions = async () => {
            try {
                const response = await axios.get(`${SERVER_ORIGIN}/api/em/auth/em/all`, {
                    params: {
                        search: '',
                        page: 1,
                        limit: 5,
                        sortBy: "name",
                        sortType: "asc"
                    },
                    headers: {
                        'auth-token': sessionStorage.getItem('token'),
                    }
                });

                const options = response.data.filteredInstitutes.map(em => ({
                    label: em.name,
                    value: em._id,
                    emData: {
                        chapterName: em.chapterName,
                        regionName: em.region,
                        emName: em.name,
                        emId: em._id
                    }
                }));
                setDefaultOptions(options);
            } catch (error) {
                console.error("Error fetching default options", error);
            }
        };

        fetchDefaultOptions();
    }, []);

    const handleEMChange = (selectedOption) => {
        setSelectedEM(selectedOption);
        if (selectedOption) {
            setTermsForm({
                ...termsForm,
                createdBy: selectedOption.value,
                emData: selectedOption.emData
            });
        } else {
            setTermsForm({
                ...termsForm,
                createdBy: "",
                emData: null
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`Input change: ${name} = ${value}`); // Debug log
        setTermsForm({
            ...termsForm,
            [name]: value
        });
    };

    // Handle content change from ReactQuill
    const handleContentChange = (content) => {
        setTermsForm({
            ...termsForm,
            content: content
        });
    };

    const handleFinancialYearChange = (selectedOption) => {
        setTermsForm({
            ...termsForm,
            financialYear: selectedOption ? selectedOption.value : ""
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!termsForm.title.trim()) {
            newErrors.title = "Title is required";
        }

        // For ReactQuill content, check if there's actual text content (not just empty HTML tags)
        const contentText = termsForm.content.replace(/<[^>]*>/g, '').trim();
        if (!contentText || contentText === '') {
            newErrors.content = "Content is required";
        }

        if (!termsForm.financialYear) {
            newErrors.financialYear = "Financial Year is required";
        }

        // if (!termsForm.createdBy || !termsForm.emData) {
        //     newErrors.createdBy = "Chapter EM selection is required";
        // }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fill in all required fields");
            return;
        }

        setIsSubmitting(true);

        try {
            // Construct the payload with emData structure
            const payload = {
                title: termsForm.title,
                content: termsForm.content,
                version: termsForm.version,
                financialYear: termsForm.financialYear,
                // emData: termsForm.emData,
                termsfor: termsForm.termsfor,
                appliesTo: termsForm.appliesTo,
                tags: termsForm.tags
            };

            const response = await fetch(
                `${SERVER_ORIGIN}/api/terms/auth/create`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": sessionStorage.getItem("token"),
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();

            if (response.status >= 400) {
                if (response.status === 409) {
                    // Conflict - duplicate data
                    const errorMessage = result.statusText || "Terms and Conditions already exist for this EM and financial year";
                    toast.error(errorMessage, {
                        duration: 6000, // Show for 6 seconds for better readability
                        style: {
                            maxWidth: '500px'
                        }
                    });

                    // Log detailed info for debugging
                    if (result.data) {
                        console.log("Duplicate detected for:", result.data);
                    }
                } else if (response.status === 404) {
                    // EM not found
                    toast.error(result.statusText || "Selected Educational Manager not found");
                } else if (response.status === 400) {
                    // Validation error
                    toast.error(result.statusText || "Please check all required fields");
                } else {
                    // Other errors
                    toast.error(result.statusText || "Failed to create Terms and Conditions");
                }
            } else {
                toast.success("Terms and Conditions created successfully");
                navigate("/terms-conditions/all");
            }
        } catch (err) {
            console.error("Error creating Terms and Conditions:", err);
            toast.error("Error creating Terms and Conditions");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate("/terms-conditions/all");
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className={css.outerDiv}>
            <SecCard>
                <BackButton text="Back to Terms & Conditions" customPath="/terms-conditions/all" />
                <h2 className="text-ff1">Add Terms and Conditions</h2>

                <div className="text-ff2" autoComplete="on">
                    <style>{`
                        .ql-editor {
                            min-height: 250px;
                            font-family: inherit;
                            font-size: 14px;
                            line-height: 1.4;
                        }
                        .ql-toolbar {
                            border-top: none !important;
                            border-left: none !important;
                            border-right: none !important;
                            border-bottom: 1px solid #ccc !important;
                        }
                        .ql-container {
                            border: none !important;
                            font-family: inherit;
                        }
                        .ql-editor.ql-blank::before {
                            font-style: italic;
                            color: #999;
                        }
                    `}</style>

                    <div className="row">
                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="title">
                                    Title <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="text"
                                    id="title"
                                    name="title"
                                    placeholder="Enter Terms and Conditions Title"
                                    value={termsForm.title}
                                    onChange={handleInputChange}
                                    maxLength={200}
                                />
                                {errors.title && <small className="text-danger">{errors.title}</small>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel}>
                                    Financial Year <span style={{ color: "red" }}>*</span>
                                </label>
                                <Select
                                    options={financialYearOptions}
                                    value={financialYearOptions.find(option => option.value === termsForm.financialYear)}
                                    onChange={handleFinancialYearChange}
                                    placeholder="Select Financial Year"
                                    isClearable
                                />
                                {errors.financialYear && <small className="text-danger">{errors.financialYear}</small>}
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: "0.8rem" }}>
                        <label className={css.instiLabel} htmlFor="termsfor">
                            Terms For <span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                            className={`form-control ${errors.termsfor ? "is-invalid" : ""}`}
                            id="termsfor"
                            name="termsfor"
                            value={termsForm.termsfor}
                            onChange={onChange}
                            required
                        >
                            <option value="">Select...</option>
                            <option value="YUVA">YUVA</option>
                            <option value="THALIR">THALIR</option>
                        </select>
                        {/* <label className={css.instiLabel} htmlFor="createdBy">
                            Chapter EM Name <span style={{ color: "red" }}>*</span>
                        </label> */}
                        {/* <AsyncSelect
                            cacheOptions
                            loadOptions={loadOptions}
                            defaultOptions={defaultOptions}
                            onChange={handleEMChange}
                            value={selectedEM}
                            placeholder="Select..."
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    borderColor: errors.createdBy ? '#dc3545' : (state.isFocused ? '#007bff' : '#ced4da'),
                                    '&:hover': {
                                        borderColor: errors.createdBy ? '#dc3545' : '#007bff'
                                    }
                                })
                            }}
                        />
                        {errors.createdBy && <small className="text-danger">{errors.createdBy}</small>} */}
                    </div>

                    <div style={{ marginBottom: "1.5rem" }}>
                        <label className={css.instiLabel} htmlFor="content">
                            Content <span style={{ color: "red" }}>*</span>
                        </label>
                        <div style={{
                            marginTop: "0.5rem",
                            border: errors.content ? '1px solid #dc3545' : '1px solid #ced4da',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: 'white'
                        }}>
                            <ReactQuill
                                theme="snow"
                                modules={quillModules}
                                formats={quillFormats}
                                value={termsForm.content}
                                onChange={handleContentChange}
                                style={{
                                    height: '300px',
                                    backgroundColor: 'white',
                                    border: 'none'
                                }}
                                placeholder="Enter Terms and Conditions content..."
                            />
                        </div>
                        <div style={{ height: '10px' }}></div>
                        {errors.content && <small className="text-danger">{errors.content}</small>}
                    </div>



                    <div style={{ textAlign: "center" }}>
                        <button
                            onClick={handleSubmit}
                            className={css.instiBtn}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Adding..." : "Add Terms and Conditions"}
                        </button>
                    </div>
                </div>
            </SecCard>
        </div>
    );
};

export default AddTermsAndConditions; 